import { ReactNode } from "react";

import { ScopeType } from "sockapi/scope-types";
import { AdminQueueTaskSummary } from "sockapi/scope-specs/admin-queue-spec";
import {
  PuzzleSubmissionType,
  AdminQueueTaskType,
} from "sockapi/scope-specs/admin-queue-task-spec";
import { useScopeStateResolvable } from "stores/ScopeStateStore";
import { useAdminPreferencesStore } from "stores/UserPreferencesStore";
import { Spinner } from "components/Loading";
import { Link } from "components/Link";

import ClaimedBy from "components/AdminQueue/ClaimedBy";
import { HintAdminQueueTaskSummaryDisplay } from "components/AdminQueue/admin-queue-hint";
import { useNavStore } from "stores/NavStore";
import { useReactiveTimeout } from "client-util/useReactiveTimeout";

export function AdminQueueTaskSummaryDisplay(props: {
  taskId: string;
  taskSummary: AdminQueueTaskSummary;
}) {
  const { taskId, taskSummary } = props;
  const adminQueueTaskScopeState = useScopeStateResolvable(
    ScopeType.ADMIN_QUEUE_TASK,
    {
      taskId,
    }
  );
  const staffId = useAdminPreferencesStore((state) => state.staffId);
  const isAdminQueuePosthuntView = useNavStore(
    (state) => state.isAdminQueuePosthuntView
  );

  const hasUnsoonzed = useReactiveTimeout(
    adminQueueTaskScopeState?.snooze?.snoozeUntil ?? null
  );

  if (adminQueueTaskScopeState === null) return <Spinner inline={true} />;
  const { task, claim, snooze } = adminQueueTaskScopeState;
  const isClaimedBySelf = claim !== undefined && claim.staffId === staffId;
  const isSnoozed = snooze !== undefined && !hasUnsoonzed;

  return (
    <div
      className={`admin-queue-task-summary ${
        taskSummary.respondTime === undefined || isAdminQueuePosthuntView
          ? isSnoozed
            ? "admin-queue-task-summary-snoozed"
            : claim !== undefined && !isClaimedBySelf
              ? "admin-queue-task-summary-claimed"
              : "admin-queue-task-summary-open"
          : "admin-queue-task-summary-closed"
      }`}
    >
      <div className="admin-queue-task-claim-control">
        <ClaimedBy
          taskId={taskId}
          taskSummary={taskSummary}
          useClaimLink={true}
        />
        <div>
          <Link route="admin_queue_task" params={{ taskId }}>
            {isClaimedBySelf ? "View" : "View without claiming"}
          </Link>
        </div>
      </div>
      <div className="admin-queue-task-summary-content">
        {((): NonNullable<ReactNode> => {
          switch (task.type) {
            case AdminQueueTaskType.HINT:
              return <HintAdminQueueTaskSummaryDisplay hintId={task.hintId} />;
            case AdminQueueTaskType.PUZZLE_SUBMISSION:
              switch (task.submissionType) {
                case PuzzleSubmissionType.DUMMY:
                  return <>Dummy</>;
              }
          }
        })()}
      </div>
    </div>
  );
}
